import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Card,
  Space,
  Button,
  Tag,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Tooltip,
  Popconfirm,
  Spin,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { ProTable } from '@ant-design/pro-components';
import {
  SearchOutlined,
  EyeOutlined,
  StopOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useUserList, useSuspendUser, useUnsuspendUser, useHardDeleteUser } from '@/hooks/useUsers';
import { usePinList } from '@/hooks/usePins';
import type { User, UserStatus, SportType } from '@/types/user';
import {
  USER_STATUS_CONFIG,
  SPORT_TYPE_CONFIG,
} from '@/config/constants';
import { UserAvatar } from '@/components/UserAvatar';
import { TierBadge } from '@/components/TierBadge';
import { ConfirmAction } from '@/components/ConfirmAction';
import type { Tier } from '@/types/user';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// 필터 위에 다는 작은 제목 라벨 — 값이 '전체'일 때도 무슨 필터인지 알 수 있게
function FilterLabel({ text }: { text: string }) {
  return (
    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
      {text}
    </Text>
  );
}

// 회원 목록 필터/페이지 상태를 sessionStorage 에 저장 — 상세 진입 후 뒤로가기로 돌아왔을 때 복원
const USERS_LIST_STATE_KEY = 'admin_users_list_state';
function loadListState(): Record<string, unknown> {
  try {
    return JSON.parse(sessionStorage.getItem(USERS_LIST_STATE_KEY) ?? '{}');
  } catch {
    return {};
  }
}

export function UserListPage() {
  const navigate = useNavigate();
  const _saved = loadListState();
  const [searchText, setSearchText] = useState<string>((_saved.searchText as string) ?? '');
  const [statusFilter, setStatusFilter] = useState<UserStatus | undefined>(
    _saved.statusFilter as UserStatus | undefined,
  );
  const [sportTypeFilter, setSportTypeFilter] = useState<SportType | undefined>(
    _saved.sportTypeFilter as SportType | undefined,
  );
  const [verifiedFilter, setVerifiedFilter] = useState<boolean | undefined>(
    _saved.verifiedFilter as boolean | undefined,
  );
  const [genderFilter, setGenderFilter] = useState<string | undefined>(
    _saved.genderFilter as string | undefined,
  );
  const [platformFilter, setPlatformFilter] = useState<string | undefined>(
    _saved.platformFilter as string | undefined,
  );
  const [providerFilter, setProviderFilter] = useState<string | undefined>(
    _saved.providerFilter as string | undefined,
  );
  // 홈그라운드(favorite_pin_id) 필터 — 핀 선택 시 라벨 유지를 위해 {value,label} 로 저장
  const [favoritePin, setFavoritePin] = useState<{ value: string; label: string } | undefined>(
    _saved.favoritePin as { value: string; label: string } | undefined,
  );
  const [pinSearchText, setPinSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[string, string] | undefined>(
    _saved.dateRange as [string, string] | undefined,
  );
  const [page, setPage] = useState<number>((_saved.page as number) ?? 1);
  const [pageSize, setPageSize] = useState<number>((_saved.pageSize as number) ?? 20);

  useEffect(() => {
    sessionStorage.setItem(
      USERS_LIST_STATE_KEY,
      JSON.stringify({
        searchText,
        statusFilter,
        sportTypeFilter,
        verifiedFilter,
        genderFilter,
        platformFilter,
        providerFilter,
        favoritePin,
        dateRange,
        page,
        pageSize,
      }),
    );
  }, [
    searchText,
    statusFilter,
    sportTypeFilter,
    verifiedFilter,
    genderFilter,
    platformFilter,
    providerFilter,
    favoritePin,
    dateRange,
    page,
    pageSize,
  ]);
  const [suspendTarget, setSuspendTarget] = useState<User | null>(null);

  // 홈그라운드 핀 검색 (서버 검색 + 디바운스)
  const { data: pinData, isFetching: pinFetching } = usePinList({
    search: pinSearchText || undefined,
    pageSize: 30,
  });
  const pinSearchTimer = useRef<ReturnType<typeof setTimeout>>();
  const handlePinSearch = (v: string) => {
    clearTimeout(pinSearchTimer.current);
    pinSearchTimer.current = setTimeout(() => setPinSearchText(v), 300);
  };

  const { data, isLoading, refetch } = useUserList({
    search: searchText || undefined,
    status: statusFilter,
    sportType: sportTypeFilter,
    isVerified: verifiedFilter,
    gender: genderFilter,
    devicePlatform: platformFilter,
    loginProvider: providerFilter,
    favoritePinId: favoritePin?.value,
    dateFrom: dateRange?.[0],
    dateTo: dateRange?.[1],
    page,
    pageSize,
  });

  const suspendMutation = useSuspendUser();
  const unsuspendMutation = useUnsuspendUser();
  const hardDeleteMutation = useHardDeleteUser();

  // 모든 검색 조건 초기화
  const handleResetFilters = () => {
    setSearchText('');
    setStatusFilter(undefined);
    setSportTypeFilter(undefined);
    setVerifiedFilter(undefined);
    setGenderFilter(undefined);
    setPlatformFilter(undefined);
    setProviderFilter(undefined);
    setFavoritePin(undefined);
    setPinSearchText('');
    setDateRange(undefined);
    setPage(1);
  };

  const columns: TableColumnsType<User> = [
    {
      title: '사용자',
      dataIndex: 'nickname',
      key: 'nickname',
      render: (nickname: string, record: User) => (
        <UserAvatar
          src={record.profileImageUrl}
          nickname={nickname}
          size="small"
        />
      ),
    },
    {
      title: '이메일',
      dataIndex: 'email',
      key: 'email',
      render: (email: string | null) => email || <Text type="secondary">소셜 로그인</Text>,
    },
    {
      title: '스포츠 프로필',
      key: 'sportsProfiles',
      render: (_, record: User) => (
        <Space wrap>
          {record.sportsProfiles?.map((sp) => (
            <span key={sp.id}>
              <TierBadge tier={sp.tier as Tier} showScore score={sp.currentScore} />
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>
                {SPORT_TYPE_CONFIG[sp.sportType]?.label ?? sp.sportType}
              </Text>
            </span>
          )) || <Text type="secondary">없음</Text>}
        </Space>
      ),
    },
    {
      title: '홈그라운드',
      key: 'favoritePin',
      render: (_, record: User) =>
        record.favoritePin ? (
          <Tag color="blue">{record.favoritePin.name}</Tag>
        ) : (
          <Text type="secondary">미설정</Text>
        ),
    },
    {
      title: '활동 핀',
      key: 'pinActivities',
      render: (_, record: User) =>
        record.pinActivities?.length ? (
          <Space wrap>
            {record.pinActivities.map((pa) => (
              <Tag key={pa.id}>{pa.pin?.name ?? '-'}</Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">없음</Text>
        ),
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      render: (status: UserStatus) => {
        const config = USER_STATUS_CONFIG[status];
        if (!config) return <Tag>{status ?? '-'}</Tag>;
        return <Tag color={config.color}>{config.label}</Tag>;
      },
      filters: Object.entries(USER_STATUS_CONFIG).map(([value, { label }]) => ({
        text: label,
        value,
      })),
      width: 90,
    },
    {
      title: '인증',
      dataIndex: 'isVerified',
      key: 'isVerified',
      render: (isVerified: boolean) =>
        isVerified ? (
          <Tag color="green">인증완료</Tag>
        ) : (
          <Tag>미인증</Tag>
        ),
      width: 90,
    },
    {
      title: '가입일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
      sorter: true,
      width: 120,
    },
    {
      title: '마지막 접속',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      render: (date: string | null) =>
        date ? (
          <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm:ss')}>
            <span>{dayjs(date).fromNow()}</span>
          </Tooltip>
        ) : (
          <Text type="secondary">없음</Text>
        ),
      width: 120,
    },
    {
      title: '액션',
      key: 'actions',
      render: (_, record: User) => (
        <Space>
          <Tooltip title="상세 보기">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/users/${record.id}`)}
            />
          </Tooltip>

          {record.status === 'ACTIVE' ? (
            <Tooltip title="정지">
              <Button
                type="text"
                danger
                icon={<StopOutlined />}
                onClick={() => setSuspendTarget(record)}
              />
            </Tooltip>
          ) : record.status === 'SUSPENDED' ? (
            <Popconfirm
              title="정지를 해제하시겠습니까?"
              onConfirm={() => unsuspendMutation.mutate(record.id)}
              okText="해제"
              cancelText="취소"
            >
              <Tooltip title="정지 해제">
                <Button type="text" icon={<CheckCircleOutlined />} style={{ color: '#52c41a' }} />
              </Tooltip>
            </Popconfirm>
          ) : null}

          <Popconfirm
            title="계정 완전 삭제"
            description={`'${record.nickname}' 계정을 완전 삭제하시겠습니까? 되돌릴 수 없습니다. (활동 이력이 있으면 차단됩니다)`}
            onConfirm={() => hardDeleteMutation.mutate(record.id)}
            okText="완전 삭제"
            okButtonProps={{ danger: true }}
            cancelText="취소"
          >
            <Tooltip title="완전 삭제">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
      width: 130,
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 4 }}>
        사용자 관리
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
        전체 사용자 목록 조회 및 관리
      </Text>

      {/* 검색/필터 영역 */}
      <Card style={{ marginBottom: 16, borderRadius: 8 }}>
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} sm={12} lg={8}>
            <FilterLabel text="검색어" />
            <Input
              placeholder="닉네임/이메일/전화번호/실명 검색"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(1);
              }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={6} lg={4}>
            <FilterLabel text="상태" />
            <Select
              placeholder="상태"
              style={{ width: '100%' }}
              value={statusFilter ?? ''}
              onChange={(val) => {
                setStatusFilter(val === '' ? undefined : (val as UserStatus));
                setPage(1);
              }}
              options={[
                { value: '', label: '전체' },
                ...Object.entries(USER_STATUS_CONFIG).map(([value, { label }]) => ({
                  value,
                  label,
                })),
              ]}
            />
          </Col>
          <Col xs={24} sm={6} lg={4}>
            <FilterLabel text="종목" />
            <Select
              placeholder="종목"
              style={{ width: '100%' }}
              value={sportTypeFilter ?? ''}
              onChange={(val) => {
                setSportTypeFilter(val === '' ? undefined : (val as SportType));
                setPage(1);
              }}
              options={[
                { value: '', label: '전체' },
                ...Object.entries(SPORT_TYPE_CONFIG).map(([value, { label }]) => ({
                  value,
                  label,
                })),
              ]}
            />
          </Col>
          <Col xs={24} sm={6} lg={4}>
            <FilterLabel text="본인인증" />
            <Select<boolean | ''>
              placeholder="인증"
              style={{ width: '100%' }}
              value={verifiedFilter ?? ''}
              onChange={(val) => {
                setVerifiedFilter(val === '' ? undefined : (val as boolean));
                setPage(1);
              }}
              options={[
                { value: '', label: '전체' },
                { value: true, label: '인증됨' },
                { value: false, label: '미인증' },
              ]}
            />
          </Col>
          <Col xs={24} sm={6} lg={4}>
            <FilterLabel text="성별" />
            <Select
              placeholder="성별"
              style={{ width: '100%' }}
              value={genderFilter ?? ''}
              onChange={(val) => {
                setGenderFilter(val === '' ? undefined : val);
                setPage(1);
              }}
              options={[
                { value: '', label: '전체' },
                { value: 'MALE', label: '남성' },
                { value: 'FEMALE', label: '여성' },
              ]}
            />
          </Col>
          <Col xs={24} sm={6} lg={4}>
            <FilterLabel text="플랫폼" />
            <Select
              placeholder="플랫폼"
              style={{ width: '100%' }}
              value={platformFilter ?? ''}
              onChange={(val) => {
                setPlatformFilter(val === '' ? undefined : val);
                setPage(1);
              }}
              options={[
                { value: '', label: '전체' },
                { value: 'IOS', label: 'iOS' },
                { value: 'ANDROID', label: 'Android' },
              ]}
            />
          </Col>
          <Col xs={24} sm={6} lg={4}>
            <FilterLabel text="로그인 방식" />
            <Select
              placeholder="로그인 방식"
              style={{ width: '100%' }}
              value={providerFilter ?? ''}
              onChange={(val) => {
                setProviderFilter(val === '' ? undefined : val);
                setPage(1);
              }}
              options={[
                { value: '', label: '전체' },
                { value: 'EMAIL', label: '이메일' },
                { value: 'KAKAO', label: '카카오' },
                { value: 'APPLE', label: 'Apple' },
                { value: 'GOOGLE', label: 'Google' },
                { value: 'NONE', label: '없음' },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <FilterLabel text="홈그라운드" />
            <Select
              labelInValue
              showSearch
              allowClear
              placeholder="홈그라운드(핀) 검색"
              style={{ width: '100%' }}
              value={favoritePin}
              filterOption={false}
              onSearch={handlePinSearch}
              onChange={(val) => {
                setFavoritePin(val ?? undefined);
                setPage(1);
              }}
              notFoundContent={pinFetching ? <Spin size="small" /> : null}
              options={(pinData?.items ?? []).map((p) => ({ value: p.id, label: p.name }))}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <FilterLabel text="가입일" />
            <RangePicker
              style={{ width: '100%' }}
              placeholder={['가입 시작일', '가입 종료일']}
              value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
              onChange={(_, dateStrings) => {
                if (dateStrings[0] && dateStrings[1]) {
                  setDateRange([dateStrings[0], dateStrings[1]]);
                } else {
                  setDateRange(undefined);
                }
                setPage(1);
              }}
            />
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Space>
              <Button onClick={() => refetch()}>새로고침</Button>
              <Button onClick={handleResetFilters}>초기화</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 사용자 테이블 */}
      <Card style={{ borderRadius: 8 }}>
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            총 {(data?.total ?? 0).toLocaleString()}명
          </Text>
        </div>

        <ProTable<User>
          columns={columns}
          dataSource={data?.items || []}
          loading={isLoading}
          rowKey="id"
          search={false}
          toolBarRender={false}
          pagination={{
            current: page,
            pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `총 ${(total ?? 0).toLocaleString()}명`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          onRow={(record) => ({
            onDoubleClick: () => navigate(`/users/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          scroll={{ x: 1050 }}
        />
      </Card>

      {/* 정지 확인 모달 */}
      <ConfirmAction
        open={!!suspendTarget}
        title={`'${suspendTarget?.nickname}' 사용자를 정지하시겠습니까?`}
        description="정지된 사용자는 서비스 이용이 제한됩니다."
        requireReason
        reasonLabel="정지 사유"
        reasonPlaceholder="정지 사유를 입력해주세요. (예: 허위 경기 결과 입력)"
        onConfirm={async (reason) => {
          if (suspendTarget && reason) {
            await suspendMutation.mutateAsync({
              id: suspendTarget.id,
              reason,
            });
            setSuspendTarget(null);
          }
        }}
        onCancel={() => setSuspendTarget(null)}
        loading={suspendMutation.isPending}
        confirmText="정지"
      />
    </div>
  );
}

